import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Delete, Refresh, Save } from '@mui/icons-material';
import { usePartsCatalog } from './parts/usePartsCatalog';
import { UNIT_METRIC_OPTIONS, unitMetricLabel } from '../constants/unitMetrics';

const DEFAULT_COMMON_VALUES = {
  unitMetric: '',
  frequency: '',
  category: '',
  manufacturerNames: [],
  primaryModels: [],
  modelVariants: [],
  partType: 'Standard Part',
  ataChapter: '',
  applicableAssemblyTypes: []
};

const newRow = (index = 1) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${index}`,
  partNumber: '',
  partName: '',
  description: ''
});

const ALLOWED_FREQUENCY = ['', 'Cycles', 'Landings', 'Hours', 'Days', 'Years'];

const newComponentRow = (index = 1) => ({
  id: `cmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${index}`,
  partNumber: '',
  code: '',
  position: '',
  serialNumber: ''
});

const QuickAdd = () => {
  const { dropdownOptions, fetchDropdownOptions } = usePartsCatalog();
  const [commonValues, setCommonValues] = useState(DEFAULT_COMMON_VALUES);
  const [rows, setRows] = useState([newRow(1)]);
  const [createComponents, setCreateComponents] = useState(false);
  const [componentRows, setComponentRows] = useState([newComponentRow(1)]);
  const [componentCommon, setComponentCommon] = useState({
    aircraftId: '',
    assemblyId: '',
    ataChapterId: '',
    manufacturerNameId: '',
    monitorTypeId: '',
    licenseId: '',
    installedOnDate: new Date().toISOString().slice(0, 10)
  });
  const [componentDropdowns, setComponentDropdowns] = useState({
    aircraft: [],
    assemblies: [],
    monitorTypes: [],
    licenses: [],
    manufacturerNames: [],
    manufacturers: [],
    ataChapters: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState({
    done: false,
    partSuccessCount: 0,
    partFailureCount: 0,
    componentSuccessCount: 0,
    componentFailureCount: 0,
    errors: []
  });

  useEffect(() => {
    fetchDropdownOptions();
  }, [fetchDropdownOptions]);

  useEffect(() => {
    const fetchComponentDropdowns = async () => {
      try {
        const [aircraftRes, monitorTypesRes, licensesRes, manufacturerNamesRes, manufacturersRes, ataChaptersRes] = await Promise.all([
          axios.get('/api/aircraft'),
          axios.get('/api/master-data/monitor-types', { params: { isActive: 'true' } }),
          axios.get('/api/master-data/licenses', { params: { isActive: 'true' } }),
          axios.get('/api/master-data/manufacturer-names', { params: { isActive: 'true' } }),
          axios.get('/api/master-data/manufacturers', { params: { isActive: 'true' } }),
          axios.get('/api/master-data/ata-chapters', { params: { isActive: 'true' } })
        ]);
        setComponentDropdowns({
          aircraft: Array.isArray(aircraftRes.data) ? aircraftRes.data : [],
          monitorTypes: Array.isArray(monitorTypesRes.data) ? monitorTypesRes.data : [],
          licenses: Array.isArray(licensesRes.data) ? licensesRes.data : [],
          manufacturerNames: Array.isArray(manufacturerNamesRes.data) ? manufacturerNamesRes.data : [],
          manufacturers: Array.isArray(manufacturersRes.data) ? manufacturersRes.data : [],
          ataChapters: Array.isArray(ataChaptersRes.data) ? ataChaptersRes.data : [],
          assemblies: []
        });
      } catch (error) {
        console.error('Error fetching component dropdowns:', error);
      }
    };
    fetchComponentDropdowns();
  }, []);

  useEffect(() => {
    const fetchAssemblies = async () => {
      if (!componentCommon.aircraftId) {
        setComponentDropdowns((prev) => ({ ...prev, assemblies: [] }));
        return;
      }
      try {
        const res = await axios.get('/api/assemblies', {
          params: {
            aircraftId: componentCommon.aircraftId,
            isActive: 'true'
          }
        });
        setComponentDropdowns((prev) => ({
          ...prev,
          assemblies: Array.isArray(res.data) ? res.data : []
        }));
      } catch (error) {
        console.error('Error fetching assemblies:', error);
      }
    };
    fetchAssemblies();
  }, [componentCommon.aircraftId]);

  const filteredPrimaryModels = useMemo(() => {
    if (!commonValues.manufacturerNames?.length) return [];
    const selectedManufacturerIds = commonValues.manufacturerNames.map((id) => String(id));
    return (dropdownOptions.primaryModels || []).filter((model) => {
      const manufacturerId = typeof model.manufacturer === 'object' && model.manufacturer !== null
        ? String(model.manufacturer._id || model.manufacturer)
        : String(model.manufacturer);
      return selectedManufacturerIds.includes(manufacturerId);
    });
  }, [commonValues.manufacturerNames, dropdownOptions.primaryModels]);

  const filteredModelVariants = useMemo(() => {
    if (!commonValues.primaryModels?.length) return [];
    return (dropdownOptions.modelVariants || []).filter((variant) => {
      const primaryModelId = typeof variant.primaryModel === 'object' && variant.primaryModel !== null
        ? variant.primaryModel._id || variant.primaryModel
        : variant.primaryModel;
      return commonValues.primaryModels.includes(primaryModelId);
    });
  }, [commonValues.primaryModels, dropdownOptions.modelVariants]);

  const updateRow = (id, key, value) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, newRow(prev.length + 1)]);
  };

  const addMultipleRows = (count) => {
    setRows((prev) => {
      const next = [...prev];
      for (let i = 0; i < count; i += 1) {
        next.push(newRow(next.length + 1));
      }
      return next;
    });
  };

  const deleteRow = (id) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((row) => row.id !== id)));
  };

  const resetPage = () => {
    setCommonValues(DEFAULT_COMMON_VALUES);
    setRows([newRow(1)]);
    setCreateComponents(false);
    setComponentRows([newComponentRow(1)]);
    setComponentCommon({
      aircraftId: '',
      assemblyId: '',
      ataChapterId: '',
      manufacturerNameId: '',
      monitorTypeId: '',
      licenseId: '',
      installedOnDate: new Date().toISOString().slice(0, 10)
    });
    setResult({
      done: false,
      partSuccessCount: 0,
      partFailureCount: 0,
      componentSuccessCount: 0,
      componentFailureCount: 0,
      errors: []
    });
  };

  const buildPayload = (row) => {
    const basePayload = {
      ...commonValues,
      ...row,
      manufacturers: commonValues.manufacturerNames?.length ? commonValues.manufacturerNames : undefined,
      primaryModels: commonValues.primaryModels?.length ? commonValues.primaryModels : undefined,
      modelVariants: commonValues.modelVariants?.length ? commonValues.modelVariants : undefined,
      applicableAssemblyTypes: commonValues.applicableAssemblyTypes || [],
      frequency: ALLOWED_FREQUENCY.includes(commonValues.frequency) ? commonValues.frequency : '',
      category: commonValues.category || undefined,
      ataChapter: commonValues.ataChapter || undefined
    };
    delete basePayload.id;
    delete basePayload.manufacturerNames;
    return basePayload;
  };

  const validateRows = () => {
    const errors = [];
    rows.forEach((row, index) => {
      if (!row.partNumber?.trim()) {
        errors.push(`Row ${index + 1}: Part Number is required`);
      }
      if (!row.description?.trim()) {
        errors.push(`Row ${index + 1}: Description is required`);
      }
    });
    return errors;
  };

  const validateComponentRows = () => {
    const errors = [];
    if (!componentCommon.aircraftId) errors.push('Components: Aircraft is required');
    if (!componentCommon.assemblyId) errors.push('Components: Assembly is required');
    if (!componentCommon.ataChapterId) errors.push('Components: ATA Chapter is required');
    if (!componentCommon.manufacturerNameId) errors.push('Components: Manufacturer is required');
    if (!componentCommon.monitorTypeId) errors.push('Components: Monitor Type is required');
    if (!componentCommon.licenseId) errors.push('Components: License is required');
    if (!componentCommon.installedOnDate) errors.push('Components: Installed Date is required');

    componentRows.forEach((row, index) => {
      if (!row.partNumber?.trim()) errors.push(`Component row ${index + 1}: Part Number is required`);
      if (!row.code?.trim()) errors.push(`Component row ${index + 1}: Component Code is required`);
      if (!row.position?.trim()) errors.push(`Component row ${index + 1}: Position is required`);
    });
    return errors;
  };

  const updateComponentRow = (id, key, value) => {
    setComponentRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  };

  const addComponentRow = () => {
    setComponentRows((prev) => [...prev, newComponentRow(prev.length + 1)]);
  };

  const addMultipleComponentRows = (count) => {
    setComponentRows((prev) => {
      const next = [...prev];
      for (let i = 0; i < count; i += 1) next.push(newComponentRow(next.length + 1));
      return next;
    });
  };

  const deleteComponentRow = (id) => {
    setComponentRows((prev) => (prev.length === 1 ? prev : prev.filter((row) => row.id !== id)));
  };

  const handleSubmit = async () => {
    const validationErrors = validateRows();
    if (createComponents) {
      validationErrors.push(...validateComponentRows());
    }
    if (validationErrors.length > 0) {
      setResult({
        done: true,
        partSuccessCount: 0,
        partFailureCount: validationErrors.length,
        componentSuccessCount: 0,
        componentFailureCount: 0,
        errors: validationErrors
      });
      return;
    }

    setIsSubmitting(true);
    setResult({
      done: false,
      partSuccessCount: 0,
      partFailureCount: 0,
      componentSuccessCount: 0,
      componentFailureCount: 0,
      errors: []
    });

    try {
      const requests = rows.map((row) => axios.post('/api/parts', buildPayload(row)));
      const settled = await Promise.allSettled(requests);

      let partSuccessCount = 0;
      const errors = [];
      const createdPartByNumber = {};
      settled.forEach((item, index) => {
        if (item.status === 'fulfilled') {
          partSuccessCount += 1;
          const createdPart = item.value?.data;
          if (createdPart?.partNumber) {
            createdPartByNumber[String(createdPart.partNumber).trim()] = createdPart;
          }
          return;
        }
        const message = item.reason?.response?.data?.message || item.reason?.message || 'Failed to create part';
        errors.push(`Row ${index + 1} (${rows[index].partNumber || 'no part number'}): ${message}`);
      });

      let componentSuccessCount = 0;
      let componentFailureCount = 0;
      if (createComponents && componentRows.length > 0) {
        const selectedManufacturer = componentDropdowns.manufacturers.find((m) => {
          const linkedNameId = m.manufacturerName?._id || m.manufacturerName;
          return String(linkedNameId) === String(componentCommon.manufacturerNameId);
        });

        if (!selectedManufacturer?._id) {
          errors.push('Components: no active Manufacturer found for selected Manufacturer Name');
          componentFailureCount = componentRows.length;
        } else {
          const componentPayloads = componentRows.map((componentRow, index) => {
            const key = String(componentRow.partNumber || '').trim();
            const part = createdPartByNumber[key];
            if (!part?._id) {
              return {
                index,
                error: `Component row ${index + 1}: part "${key}" was not created in this batch`
              };
            }
            const sourcePartRow = rows.find((r) => String(r.partNumber).trim() === key);
            return {
              index,
              payload: {
                aircraftId: componentCommon.aircraftId,
                assemblyId: componentCommon.assemblyId,
                code: componentRow.code.trim(),
                ataChapterId: componentCommon.ataChapterId,
                partId: part._id,
                partNumber: part.partNumber,
                description: (sourcePartRow?.description || part.description || '').trim(),
                serialNumber: (componentRow.serialNumber || '').trim(),
                position: componentRow.position.trim(),
                manufacturerId: selectedManufacturer._id,
                monitorTypeId: componentCommon.monitorTypeId,
                installedOnDate: componentCommon.installedOnDate,
                licenseId: componentCommon.licenseId
              }
            };
          });

          const ready = componentPayloads.filter((c) => c.payload);
          componentPayloads.filter((c) => c.error).forEach((c) => errors.push(c.error));
          componentFailureCount += componentPayloads.filter((c) => c.error).length;

          const componentSettled = await Promise.allSettled(
            ready.map((c) => axios.post('/api/components', c.payload))
          );
          componentSettled.forEach((item, ix) => {
            const rowIndex = ready[ix].index;
            if (item.status === 'fulfilled') {
              componentSuccessCount += 1;
            } else {
              componentFailureCount += 1;
              const message = item.reason?.response?.data?.message
                || item.reason?.response?.data?.errors?.[0]?.msg
                || item.reason?.message
                || 'Failed to create component';
              errors.push(`Component row ${rowIndex + 1}: ${message}`);
            }
          });
        }
      }

      setResult({
        done: true,
        partSuccessCount,
        partFailureCount: rows.length - partSuccessCount,
        componentSuccessCount,
        componentFailureCount,
        errors
      });

      if (partSuccessCount > 0) {
        setRows((prev) => prev.map((row, index) => {
          if (settled[index].status === 'fulfilled') {
            return newRow(index + 1);
          }
          return row;
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4">Quick Add</Typography>
          <Typography variant="body2" color="text.secondary">
            Set common values once and create many parts, and optionally linked components, in one submission.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={resetPage} disabled={isSubmitting}>
            Reset
          </Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? 'Saving...'
              : `Create ${rows.length} Part${rows.length > 1 ? 's' : ''}${createComponents ? ` + ${componentRows.length} Component${componentRows.length > 1 ? 's' : ''}` : ''}`}
          </Button>
        </Stack>
      </Box>

      {result.done && (
        <Alert severity={(result.partFailureCount + result.componentFailureCount) > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
          Parts: {result.partSuccessCount} created, {result.partFailureCount} failed.
          {createComponents ? ` Components: ${result.componentSuccessCount} created, ${result.componentFailureCount} failed.` : ''}
        </Alert>
      )}

      {!!result.errors.length && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 220, overflow: 'auto' }}>
          {result.errors.map((error) => (
            <Typography key={error} variant="body2" color="error" sx={{ mb: 0.5 }}>
              {error}
            </Typography>
          ))}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Common Fields</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Unit Metric</InputLabel>
              <Select
                value={commonValues.unitMetric}
                label="Unit Metric"
                onChange={(e) => setCommonValues((prev) => ({ ...prev, unitMetric: e.target.value }))}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {UNIT_METRIC_OPTIONS.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unitMetricLabel(unit)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={commonValues.frequency}
                label="Frequency"
                onChange={(e) => setCommonValues((prev) => ({ ...prev, frequency: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Cycles">Cycles</MenuItem>
                <MenuItem value="Landings">Landings</MenuItem>
                <MenuItem value="Hours">Hours</MenuItem>
                <MenuItem value="Days">Days</MenuItem>
                <MenuItem value="Years">Years</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Part Type</InputLabel>
              <Select
                value={commonValues.partType}
                label="Part Type"
                onChange={(e) => setCommonValues((prev) => ({ ...prev, partType: e.target.value }))}
              >
                <MenuItem value="Vendor Part">Vendor Part</MenuItem>
                <MenuItem value="OEM Part">OEM Part</MenuItem>
                <MenuItem value="Alternate Part">Alternate Part</MenuItem>
                <MenuItem value="Superseed Part">Superseed Part</MenuItem>
                <MenuItem value="Variant Part">Variant Part</MenuItem>
                <MenuItem value="Standard Part">Standard Part</MenuItem>
                <MenuItem value="PMA Part">PMA Part</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={commonValues.category}
                label="Category"
                onChange={(e) => setCommonValues((prev) => ({ ...prev, category: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {(dropdownOptions.categories || []).map((category) => (
                  <MenuItem key={category._id} value={category._id}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={dropdownOptions.manufacturerNames || []}
              getOptionLabel={(option) => option.name || ''}
              value={(dropdownOptions.manufacturerNames || []).filter((m) => (commonValues.manufacturerNames || []).includes(m._id))}
              onChange={(event, values) => {
                const ids = values.map((value) => value._id);
                setCommonValues((prev) => ({
                  ...prev,
                  manufacturerNames: ids,
                  primaryModels: [],
                  modelVariants: []
                }));
              }}
              renderInput={(params) => <TextField {...params} label="Manufacturers" />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={filteredPrimaryModels}
              getOptionLabel={(option) => option.name || ''}
              value={filteredPrimaryModels.filter((m) => (commonValues.primaryModels || []).includes(m._id))}
              onChange={(event, values) => {
                const ids = values.map((value) => value._id);
                setCommonValues((prev) => ({
                  ...prev,
                  primaryModels: ids,
                  modelVariants: []
                }));
              }}
              renderInput={(params) => <TextField {...params} label="Primary Models" />}
              disabled={!commonValues.manufacturerNames.length}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={filteredModelVariants}
              getOptionLabel={(option) => option.name || ''}
              value={filteredModelVariants.filter((m) => (commonValues.modelVariants || []).includes(m._id))}
              onChange={(event, values) => {
                const ids = values.map((value) => value._id);
                setCommonValues((prev) => ({ ...prev, modelVariants: ids }));
              }}
              renderInput={(params) => <TextField {...params} label="Model Variants" />}
              disabled={!commonValues.primaryModels.length}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={dropdownOptions.ataChapters || []}
              getOptionLabel={(option) => `${option.code || ''} - ${option.name || ''}`}
              value={(dropdownOptions.ataChapters || []).find((ata) => ata._id === commonValues.ataChapter) || null}
              onChange={(event, value) => {
                setCommonValues((prev) => ({ ...prev, ataChapter: value?._id || '' }));
              }}
              renderInput={(params) => <TextField {...params} label="ATA Chapter" />}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={dropdownOptions.assemblyTypes || []}
              getOptionLabel={(option) => option.name || ''}
              value={(dropdownOptions.assemblyTypes || []).filter((a) => (commonValues.applicableAssemblyTypes || []).includes(a._id))}
              onChange={(event, values) => {
                const ids = values.map((value) => value._id);
                setCommonValues((prev) => ({ ...prev, applicableAssemblyTypes: ids }));
              }}
              renderInput={(params) => <TextField {...params} label="Applicable Assembly Types" />}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Variable Rows</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => addMultipleRows(10)}>Add 10 Rows</Button>
            <Button size="small" variant="contained" startIcon={<Add />} onClick={addRow}>Add Row</Button>
          </Stack>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 70 }}>#</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Part Number *</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Part Name</TableCell>
              <TableCell>Description *</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.partNumber}
                    onChange={(e) => updateRow(row.id, 'partNumber', e.target.value)}
                    placeholder="e.g. PN-001"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.partName}
                    onChange={(e) => updateRow(row.id, 'partName', e.target.value)}
                    placeholder="Optional"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.description}
                    onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                    placeholder="Part description"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton color="error" onClick={() => deleteRow(row.id)} disabled={rows.length === 1}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch checked disabled />}
            label={
              <Typography variant="body2">
                Common fields are applied to all rows
              </Typography>
            }
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip label={`Rows: ${rows.length}`} size="small" />
            <Chip label="* Required fields: Part Number, Description" size="small" />
          </Stack>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Linked Components (Optional)</Typography>
          <FormControlLabel
            control={<Switch checked={createComponents} onChange={(e) => setCreateComponents(e.target.checked)} />}
            label="Create components with parts"
          />
        </Box>

        {createComponents && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Aircraft</InputLabel>
                  <Select
                    value={componentCommon.aircraftId}
                    label="Aircraft"
                    onChange={(e) => setComponentCommon((prev) => ({ ...prev, aircraftId: e.target.value, assemblyId: '' }))}
                  >
                    {(componentDropdowns.aircraft || []).map((a) => (
                      <MenuItem key={a._id} value={a._id}>{a.registration || a.registrationNumber || a.name || a._id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Assembly</InputLabel>
                  <Select
                    value={componentCommon.assemblyId}
                    label="Assembly"
                    onChange={(e) => setComponentCommon((prev) => ({ ...prev, assemblyId: e.target.value }))}
                  >
                    {(componentDropdowns.assemblies || []).map((a) => (
                      <MenuItem key={a._id} value={a._id}>
                        {(a.type?.name || a.typeName || 'Assembly')} - SN: {a.serialNumber || 'N/A'} - Pos: {a.position || 'N/A'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>ATA Chapter</InputLabel>
                  <Select
                    value={componentCommon.ataChapterId}
                    label="ATA Chapter"
                    onChange={(e) => setComponentCommon((prev) => ({ ...prev, ataChapterId: e.target.value }))}
                  >
                    {(componentDropdowns.ataChapters || []).map((ata) => (
                      <MenuItem key={ata._id} value={ata._id}>{ata.code} - {ata.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Manufacturer</InputLabel>
                  <Select
                    value={componentCommon.manufacturerNameId}
                    label="Manufacturer"
                    onChange={(e) => setComponentCommon((prev) => ({ ...prev, manufacturerNameId: e.target.value }))}
                  >
                    {(componentDropdowns.manufacturerNames || []).map((m) => (
                      <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Monitor Type</InputLabel>
                  <Select
                    value={componentCommon.monitorTypeId}
                    label="Monitor Type"
                    onChange={(e) => setComponentCommon((prev) => ({ ...prev, monitorTypeId: e.target.value }))}
                  >
                    {(componentDropdowns.monitorTypes || []).map((m) => (
                      <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>License</InputLabel>
                  <Select
                    value={componentCommon.licenseId}
                    label="License"
                    onChange={(e) => setComponentCommon((prev) => ({ ...prev, licenseId: e.target.value }))}
                  >
                    {(componentDropdowns.licenses || []).map((l) => (
                      <MenuItem key={l._id} value={l._id}>{l.name || l.licenseNumber || l._id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Installed Date"
                  type="date"
                  value={componentCommon.installedOnDate}
                  onChange={(e) => setComponentCommon((prev) => ({ ...prev, installedOnDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1">Component Rows</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => addMultipleComponentRows(10)}>Add 10 Rows</Button>
                <Button size="small" variant="contained" startIcon={<Add />} onClick={addComponentRow}>Add Row</Button>
              </Stack>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 70 }}>#</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Part Number *</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Component Code *</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Position *</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Serial Number</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {componentRows.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.partNumber}
                        onChange={(e) => updateComponentRow(row.id, 'partNumber', e.target.value)}
                        placeholder="Must match part row"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.code}
                        onChange={(e) => updateComponentRow(row.id, 'code', e.target.value)}
                        placeholder="e.g. ENG-LH-001"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.position}
                        onChange={(e) => updateComponentRow(row.id, 'position', e.target.value)}
                        placeholder="e.g. LH"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.serialNumber}
                        onChange={(e) => updateComponentRow(row.id, 'serialNumber', e.target.value)}
                        placeholder="Optional"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="error" onClick={() => deleteComponentRow(row.id)} disabled={componentRows.length === 1}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default QuickAdd;
